import { UseMutateFunction, useMutation } from '@tanstack/react-query';
import { Form, Input, InputRef, Space } from 'antd';
import { AxiosResponse } from 'axios';
import dayjs from 'dayjs';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

import { ISearchRouteComboBox } from 'apis/GraphQL/Queries/saleLineQuery';
import { SaleTeamApi } from 'apis/SaleTeamApi';
import { User3Icon } from 'assets/svg';
import { ButtonAction, SelectForm, Switch } from 'components';
import LazySelectForm, {
  ILazySelectParams,
  initLazySelectData,
} from 'components/Select/LazySelectForm';
import TableComponent from 'components/Table/TableComponent';
import { useUser } from 'contexts/UserProvider';
import useQueryParam from 'hooks/useQueryPrams';
import { ColumnsTypeCustom } from 'interfaces/Table/ColumnType';
import { IFilterTeamSaleCb } from 'interfaces/TeamSale/TeamSale';
import Content from 'layouts/Content';
import { HocChangePagination } from 'utils/HocChangePagination';
import { DEFAULT_PAGE_SIZE } from 'utils/constants/constant';
import { formatCurrency } from 'utils/constants/functions';
import { icons } from 'utils/constants/icons';
import DrawerSaleLineTeam from './DrawerSaleLineTeam';

interface IProps {
  data: any;
  edit: number;
  loading?: boolean;
  plantData: any;
  pageInfo?: any;
  page: number;
  pageSize: number;
  route: {
    isPending: boolean;
    data: { options: any; totalItems: number };
    mutate: UseMutateFunction<
      AxiosResponse<any, any>,
      Error,
      ISearchRouteComboBox,
      unknown
    >;
  };
  setData: (data: any) => void;
  setEdit: (data: any) => void;
  onSave({ values, onSuccess }: { values: any; onSuccess?: () => void }): void;
}

const { SaveTwoTone, CloseCircleTwoTone } = icons;

const newData = {
  id: null,
  plantID: undefined,
  tteamSalesID: undefined,
  ttripID: undefined,
  ttripName: undefined,
  trouteID: undefined,
  trouteName: undefined,
  tteamSalesName: undefined,
  expectedRevenue: 0,
  actualRevenue: 0,
  useYn: true,
};

const TableSaleLineTeam = forwardRef((props: IProps, ref: any) => {
  const { user } = useUser();
  const navigate = useNavigate();

  const {
    data,
    setData,
    edit,
    setEdit,
    onSave,
    plantData,
    loading,
    route,
    pageInfo,
  } = props;

  const [form] = Form.useForm(),
    inputRef = useRef<InputRef>(null),
    [openDrawerSaleLineTeam, setOpenDrawerSaleLineTeam] = useState(false),
    [routeId, setRouteID] = useState(false),
    [teamSaleData, setTeamSaleData] = useState(initLazySelectData),
    [teamsaleId, setTeamSaleId] = useState(false),
    [isAddRow, setIsAddRow] = useState(false);

  const queryParam = useQueryParam(),
    page = parseInt(queryParam.get('page') + '') || 1,
    pageSize = parseInt(queryParam.get('page_size') + '') || DEFAULT_PAGE_SIZE;

  const { isPending: isGetSaleTeamsPending, mutate: getSaleTeams } =
    useMutation({
      mutationFn: (params: IFilterTeamSaleCb) =>
        SaleTeamApi.getAllTeamSaleCb({ ...params, useYn: true }),
      onSuccess: (res) => {
        if (res?.data?.getTeamSales)
          setTeamSaleData({
            options: res?.data?.getTeamSales?.items ?? [],
            totalItems: res?.data?.getTeamSales?.pageInfo?.totalItems ?? 0,
          });
      },
      onError: (error) => {
        console.log(error);
      },
    });

  useEffect(() => {
    if (edit) {
      inputRef.current?.focus({ cursor: 'start' });
    }
  }, [data]);

  const renderValueTable = (value: any) => value || '-';

  const statusData = [
    { label: 'Hoạt động', value: true },
    { label: 'Không hoạt động', value: false },
  ];

  const handleOptionPlantChange = (value: any) => {
    form.setFieldsValue({
      plantID: value,
      tteamSalesID: '',
      ttripID: '',
      ttripName: '',
      trouteID: '',
      trouteName: '',
      tteamSalesName: '',
    });

    getSaleTeams({ plantId: value, pageSize: 10 });
    route.mutate({ plantId: value, pageSize: 10 });
  };

  const handleOptionTeamChange = (_: any, option: any) => {
    const value = form.getFieldsValue();

    form.setFieldsValue({
      ...value,
      tteamSalesName: option?.title,
      tteamSalesID: option?.value,
    });

    if (value.plantID !== '' && typeof value.plantID !== 'undefined') return;

    const currentTeam = teamSaleData.options.find(
      (item: any) => item.id === option?.value,
    );

    if (typeof currentTeam !== 'undefined') {
      form.setFieldValue('plantID', currentTeam?.plantId);
      form.validateFields();
      route.mutate({ plantId: currentTeam?.plantId, pageSize: 10 });
      getSaleTeams({ plantId: currentTeam?.plantId, pageSize: 10 });
    }
  };

  const handleOptionRouteChange = (_: any, option: any) => {
    const value = form.getFieldsValue();

    form.setFieldsValue({
      ...value,
      trouteName: option?.title,
      trouteID: option?.label,
    });

    if (value.plantID !== '' && typeof value.plantID !== 'undefined') return;

    const currentRoute = route.data.options.find(
      (item: any) => item.id === option?.label,
    );
    const currentPlantId = currentRoute?.tplant?.id ?? undefined;

    if (typeof currentRoute !== 'undefined') {
      form.setFieldValue('plantID', currentPlantId);
      form.validateFields();
      getSaleTeams({ plantId: currentPlantId, pageSize: 10 });
      route.mutate({ plantId: currentPlantId, pageSize: 10 });
    }
  };

  const handleSaleTeamScrollChange = (params: ILazySelectParams) => {
    if (typeof user === 'undefined') return;

    const { pageSize, value } = params;
    const values = form.getFieldsValue();

    getSaleTeams({
      ...(user.isAdmin || user.isHO
        ? { plantId: values?.plantID ?? undefined }
        : { plantId: user?.plantCode }),
      ...(user?.isSale && { userId: user?.id }),
      pageSize: pageSize,
      value: value,
    });
  };

  const handleRouteScrollChange = (params: ILazySelectParams) => {
    if (typeof user === 'undefined') return;

    const { pageSize, value } = params;
    const values = form.getFieldsValue();

    if (user.isAdmin || user.isHO) {
      route.mutate({
        pageSize: pageSize,
        value: value,
        plantId: values?.plantID ?? undefined,
      });
      return;
    }

    route.mutate({
      pageSize: pageSize,
      value: value,
      plantId: values?.plantID ?? user?.plantCode,
    });
  };

  const columns: ColumnsTypeCustom = [
    {
      title: 'STT',
      dataIndex: 'stt',
      width: 50,
      className: 'text-left align-top',
      render: (_: any, __: any, index: number) =>
        index + 1 + props.pageSize * (props.page - 1),
    },
    {
      className: 'text-left align-top',
      title: 'Chi nhánh',
      dataIndex: 'plantId',
      width: 100,
      render: (value: number, _, index: number) =>
        index == edit ? (
          <Form.Item
            name={'plantID'}
            initialValue={value}
            className="m-0"
            rules={[
              {
                validator: (_, value) => {
                  if (typeof value !== 'undefined' && value !== '')
                    return Promise.resolve();
                  else return Promise.reject();
                },
                message: 'Chọn chi nhánh',
              },
            ]}
          >
            <SelectForm
              size="middle"
              defaultValue={value}
              allowClear={true}
              options={plantData.map((item: any) => ({
                label: item.id,
                value: item.id,
                key: item.id,
              }))}
              onChange={handleOptionPlantChange}
              disabled={user?.isCN || user?.isSale}
            />
          </Form.Item>
        ) : (
          value
        ),
    },
    {
      title: 'Mã đội',
      dataIndex: 'tteamSalesID',
      className: 'align-top',
      width: 150,
      render: (value: any, _, index: number) => {
        return index == edit ? (
          <Form.Item
            name={'tteamSalesID'}
            initialValue={value}
            rules={[{ required: true, message: 'Chọn mã đội' }]}
            className="m-0"
          >
            <LazySelectForm
              size="middle"
              value={value}
              data={{
                ...teamSaleData,
                options: teamSaleData.options.map((item) => ({
                  title: item?.name ?? '',
                  label: item?.subId ?? '',
                  value: item?.id ?? '',
                  key: item?.id ?? '',
                })),
              }}
              loading={isGetSaleTeamsPending}
              onChange={handleOptionTeamChange}
              onSelectScrollChange={handleSaleTeamScrollChange}
            />
          </Form.Item>
        ) : (
          value
        );
      },
    },
    {
      title: 'Mã chuyến',
      dataIndex: 'ttripID',
      className: 'align-top',
      width: 100,
    },
    {
      title: 'Tên chuyến',
      dataIndex: 'ttripName',
      className: 'align-top',
      width: 180,
    },
    {
      title: 'Mã tuyến',
      dataIndex: 'trouteID',
      width: 150,
      className: 'text-left align-top',
      render: (value: any, _, index: number) =>
        index == edit ? (
          <Form.Item
            name={'trouteID'}
            initialValue={value}
            rules={[{ required: true, message: 'Chọn mã tuyến' }]}
            className="m-0"
          >
            <LazySelectForm
              size="middle"
              data={{
                ...route.data,
                options: route.data.options.map((item: any) => {
                  const value = item?.id ?? '';

                  return {
                    title: item?.name ?? '',
                    label: value,
                    value: value,
                    key: value,
                  };
                }),
              }}
              loading={route.isPending}
              onChange={handleOptionRouteChange}
              onSelectScrollChange={handleRouteScrollChange}
            />
          </Form.Item>
        ) : (
          value
        ),
    },
    {
      title: 'Tên tuyến',
      dataIndex: 'trouteName',
      className: 'align-top',
      width: 220,
      render: (value: any, _, index: number) =>
        index == edit ? (
          <Form.Item name={'trouteName'} initialValue={value} className="m-0">
            <Input disabled />
          </Form.Item>
        ) : (
          value
        ),
    },
    {
      title: 'Tên đội',
      dataIndex: 'tteamSalesName',
      className: 'align-top',
      width: 220,
      render: (value: any, _, index: number) =>
        index == edit ? (
          <Form.Item
            name={'tteamSalesName'}
            initialValue={value}
            className="m-0"
          >
            <Input disabled />
          </Form.Item>
        ) : (
          value
        ),
    },
    {
      currency: 'vi-VN',
      title: 'Doanh thu chuyến dự kiến',
      className: 'align-top',
      dataIndex: 'expectedRevenue',
      width: 195,
      align: 'right',
      render: (value: number, _) =>
        value
          ? value === 0
            ? value
            : formatCurrency({ value, round: true })
          : '-',
    },
    {
      className: 'text-left align-top',
      title: 'Tiền tệ',
      dataIndex: 'unitPrice',
      width: 100,
      render: (value: number, _: any, index: number) =>
        index == edit ? (
          <Form.Item
            name={'unitPrice'}
            initialValue={value ?? 'triệu VND'}
            className="m-0"
          >
            <Input disabled readOnly maxLength={15} />
          </Form.Item>
        ) : (
          renderValueTable(value)
        ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'useYn',
      className: 'text-left align-top',
      width: 150,
      isShowRender: true,
      render: (value: any, _: any, index: number) =>
        index == edit ? (
          <Form.Item
            name={'useYn'}
            rules={[{ required: true, message: 'Chưa chọn trạng thái' }]}
            className="m-0"
            initialValue={value}
          >
            <SelectForm
              size="middle"
              allowClear={true}
              options={statusData.map((item: any) => ({
                label: item.label,
                value: item.value,
                key: item.value,
              }))}
              disabled
            />
          </Form.Item>
        ) : value ? (
          'Hoạt động'
        ) : (
          'Không hoạt động'
        ),
    },
    {
      title: 'Người tạo',
      dataIndex: 'tcreateUser',
      width: 160,
      className: 'text-left align-top',
      render: (value, _, index) =>
        index == edit ? (
          <Form.Item
            name={'tmodifyUser'}
            initialValue={user?.fullName}
            className="m-0"
          >
            <Input disabled />
          </Form.Item>
        ) : (
          renderValueTable(value)
        ),
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'createDate',
      type: 'date',
      width: 180,
      render: (value: any) =>
        value && dayjs(value).format('DD/MM/YYYY HH:mm:ss'),
    },
    {
      title: 'Người cập nhật',
      dataIndex: 'tmodifyUser',
      className: 'text-left',
      width: 160,
      render: (value) => renderValueTable(value),
    },
    {
      title: 'Ngày cập nhật',
      dataIndex: 'modifyDate',
      type: 'date',
      width: 180,
      render: (value: any) =>
        value && dayjs(value).format('DD/MM/YYYY HH:mm:ss'),
    },
    {
      fixed: 'right',
      className: 'text-left',
      align: 'center',
      width: 150,
      render: (_: any, record: any, index) =>
        edit == index ? (
          <Space size={'middle'} className="text-lg">
            <ButtonAction tooltip="Lưu" onClick={() => handleSave(record)}>
              <SaveTwoTone style={{ fontSize: 20 }} />
            </ButtonAction>
            <ButtonAction tooltip="Hủy" onClick={handleCancel}>
              <CloseCircleTwoTone style={{ fontSize: 20 }} />
            </ButtonAction>
          </Space>
        ) : (
          <Space size={'middle'} className="text-lg ">
            <ButtonAction
              tooltip="Khách hàng"
              disabled={edit !== -1}
              onClick={() => {
                setOpenDrawerSaleLineTeam(!openDrawerSaleLineTeam);
                setTeamSaleId(record?.teamId);
                setRouteID(record?.trouteID);
              }}
            >
              <User3Icon />
            </ButtonAction>
            <Switch
              size="small"
              onChange={() => handleSwitch(record)}
              value={record.useYn}
              disabled={edit !== -1}
            />
          </Space>
        ),
    },
  ];

  useImperativeHandle(ref, () => ({
    getData,
    getHeaderRow,
    handleAddRow,
  }));

  const getHeaderRow = () => {
    columns.shift();
    columns.pop();
    return columns;
  };

  const checkFormValidate = async () => {
    try {
      await form.validateFields();
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleAddRow = () => {
    const handleInitData = () => {
      form.resetFields();

      if (user?.isCN || user?.isSale) {
        page === 1 &&
          setData([{ ...newData, plantID: user.plantCode }, ...data]);
        form.setFieldValue('plantID', user.plantCode);
      } else if (user?.isAdmin || user?.isHO)
        page === 1 && setData([newData, ...data]);
    };

    if (page === 1) {
      form.resetFields();
      setEdit(0);

      handleInitData();
      return;
    }

    handleInitData();

    form.resetFields();
    setIsAddRow(true);
    navigate(`?page=1&page_size=${pageSize}`);
  };

  useEffect(() => {
    if (isAddRow) {
      user?.isCN || user?.isSale
        ? setData([{ ...newData, plantID: user.plantCode }, ...data])
        : setData([newData, ...data]);
      setEdit(0);
      setIsAddRow(false);
    }
  }, [data]);

  const handleSave = async (record: any) => {
    if (!(await checkFormValidate())) return;

    const findItemById = (items: any[], id: string) =>
      items.find((item) => item.id === id);

    const { tteamSalesID, trouteID } = form.getFieldsValue();

    const currentTeamSale = findItemById(teamSaleData.options, tteamSalesID);
    const currentRoute = findItemById(route.data.options, trouteID);

    onSave({
      values: {
        ...record,
        ...form.getFieldsValue(),
        teamSaleName: currentTeamSale?.name ?? '',
        routeName: currentRoute?.name ?? '',
        teamSaleSubId: currentTeamSale?.subId ?? '',
      },
      onSuccess: handleClear,
    });
  };

  const handleCancel = () => {
    edit == 0 && !data[0]?.id && setData(data.slice(1));
    handleClear();
  };

  const handleClear = () => {
    setEdit(-1);
    form.resetFields();
  };

  const getData = () => ({ headerRow: columns, datas: data });

  const handleSwitch = (record: any) => {
    onSave({
      values: {
        ...record,
        id: record.id,
        useYn: !record.useYn,
        tteamSalesID: record?.teamId,
        teamSaleSubId: record?.teamSaleId,
        type: 'switch',
      },
    });
  };

  return (
    <Content>
      <Form form={form}>
        <TableComponent
          dataSource={data}
          columns={columns}
          loading={loading}
          pagination={{
            current: pageInfo?.currentPage,
            pageSize: pageInfo?.itemsPerPage,
            total: pageInfo?.totalItems,
            disabled: edit !== -1,
            onChange: HocChangePagination(),
          }}
        />
        <DrawerSaleLineTeam
          open={openDrawerSaleLineTeam}
          setOpen={setOpenDrawerSaleLineTeam}
          routeId={routeId}
          teamsaleId={teamsaleId}
        />
      </Form>
    </Content>
  );
});

export default TableSaleLineTeam;
